import React, { useState, useEffect } from 'react';
import { exportEvaluationToPDF } from './pdfExport';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../firebaseInit';
import { collection, getDocs, doc, updateDoc, query, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { getCallTypes, getCallTypeLabel, formatUserName } from '../../utils/industryConfig';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { createAutoReport } from '../../utils/autoProblemReport';
import Modal from '../shared/Modal';
import Button from '../shared/Button';

export default function EvaluationReport({ evaluation, onClose, onEvaluationUpdate }) {
  const { teamData } = useData();
  const { userRole, userEmail, userTeam } = useAuth();
  const isAdmin = userRole === 'admin';
  const industryId = teamData?.industryId;
  const callTypes = getCallTypes(industryId);
  const [showRecategorize, setShowRecategorize] = useState(false);
  const [newCallType, setNewCallType] = useState(evaluation?.callType || callTypes[0]?.value);
  const [isRecategorizing, setIsRecategorizing] = useState(false);
  const [recategorizeError, setRecategorizeError] = useState(null);
  const [recategorizeSuccess, setRecategorizeSuccess] = useState(null);
  const [linkedLead, setLinkedLead] = useState(null);
  const [loadingLinkedLead, setLoadingLinkedLead] = useState(false);
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [linkingLead, setLinkingLead] = useState(false);

  const salespersonDisplayName = (evaluation.salespersonName && evaluation.salespersonName !== evaluation.salesperson)
    ? evaluation.salespersonName
    : formatUserName({ email: evaluation.salesperson });

  // Load recent leads for linking
  useEffect(() => {
    if (!userTeam || !db || !showLeadPicker) return;
    let mounted = true;
    const loadLeads = async () => {
      setLoadingLeads(true);
      try {
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        const allLeads = [];
        for (const userDoc of usersSnapshot.docs) {
          const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
          const leadsQuery = query(collection(db, leadsPath), orderBy('timestamp', 'desc'), firestoreLimit(20));
          const leadsSnapshot = await getDocs(leadsQuery);
          leadsSnapshot.docs.forEach(leadDoc => {
            const data = leadDoc.data();
            allLeads.push({
              id: leadDoc.id,
              path: `${leadsPath}/${leadDoc.id}`,
              userDocId: userDoc.id,
              ...data,
              timestamp: data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : null)
            });
          });
        }
        allLeads.sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0));
        if (mounted) setRecentLeads(allLeads.slice(0, 50));
      } catch (error) {
        console.warn('Failed to load leads for linking:', error);
      } finally {
        if (mounted) setLoadingLeads(false);
      }
    };
    loadLeads();
    return () => { mounted = false; };
  }, [userTeam, showLeadPicker]);

  // Link a lead to this evaluation (bidirectional)
  const handleLinkLead = async (leadId) => {
    if (!db || !userTeam || !leadId || linkingLead) return;
    setLinkingLead(true);
    try {
      const selectedLead = recentLeads.find(l => l.id === leadId);
      // Update evaluation with lead link
      const evalRef = doc(db, `artifacts/my-real-estate-assistant/teams/${userTeam}/call-evaluations/${evaluation.id}`);
      await updateDoc(evalRef, { linkedLeadId: leadId });
      // Update lead with evaluation link
      if (selectedLead?.path) {
        await updateDoc(doc(db, selectedLead.path), { linkedEvaluationId: evaluation.id });
      }
      // Update local state
      setLinkedLead(selectedLead);
      evaluation.linkedLeadId = leadId; // Update in-memory reference
      setShowLeadPicker(false);
      if (onEvaluationUpdate) onEvaluationUpdate();
    } catch (err) {
      console.error('Failed to link lead:', err);
    } finally {
      setLinkingLead(false);
    }
  };

  // Unlink lead from this evaluation (bidirectional)
  const handleUnlinkLead = async () => {
    if (!db || !userTeam || linkingLead) return;
    setLinkingLead(true);
    try {
      const evalRef = doc(db, `artifacts/my-real-estate-assistant/teams/${userTeam}/call-evaluations/${evaluation.id}`);
      await updateDoc(evalRef, { linkedLeadId: null });
      // Also remove the reverse link from the lead
      if (linkedLead?.path) {
        await updateDoc(doc(db, linkedLead.path), { linkedEvaluationId: null });
      } else if (evaluation.linkedLeadId) {
        // Search for the lead to unlink it
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        for (const userDoc of usersSnapshot.docs) {
          const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
          const leadsSnapshot = await getDocs(collection(db, leadsPath));
          const match = leadsSnapshot.docs.find(d => d.id === evaluation.linkedLeadId);
          if (match) {
            await updateDoc(doc(db, `${leadsPath}/${match.id}`), { linkedEvaluationId: null });
            break;
          }
        }
      }
      setLinkedLead(null);
      evaluation.linkedLeadId = null;
      if (onEvaluationUpdate) onEvaluationUpdate();
    } catch (err) {
      console.error('Failed to unlink lead:', err);
    } finally {
      setLinkingLead(false);
    }
  };

  // Load linked lead data if it exists
  useEffect(() => {
    if (!evaluation?.linkedLeadId || !userTeam || !db) return;

    let mounted = true;
    const loadLinkedLead = async () => {
      setLoadingLinkedLead(true);
      try {
        // Search through team members' leads to find the linked one
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        
        for (const userDoc of usersSnapshot.docs) {
          const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
          const leadsSnapshot = await getDocs(collection(db, leadsPath));
          const matchedDoc = leadsSnapshot.docs.find(d => d.id === evaluation.linkedLeadId);
          if (matchedDoc) {
            const data = matchedDoc.data();
            if (mounted) {
              setLinkedLead({
                id: matchedDoc.id,
                ...data,
                timestamp: data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : null)
              });
            }
            break;
          }
        }
      } catch (error) {
        console.warn('Failed to load linked lead:', error);
      } finally {
        if (mounted) setLoadingLinkedLead(false);
      }
    };

    loadLinkedLead();
    return () => { mounted = false; };
  }, [evaluation?.linkedLeadId, userTeam]);

  if (!evaluation || !evaluation.evaluation) {
    return null;
  }
  
  const { evaluation: evalData } = evaluation;
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Use dynamic labels from industryConfig
  const callTypeLabels = Object.fromEntries(
    callTypes.map(ct => [ct.value, ct.label])
  );

  // Convert categoryScores to array if it's an object
  const getCategoryScoresArray = () => {
    if (!evalData.categoryScores) return [];
    
    // If it's already an array, return it (and normalize maxScore)
    if (Array.isArray(evalData.categoryScores)) {
      return evalData.categoryScores.map(cat => ({
        ...cat,
        maxScore: 10 // Category scores are always out of 10
      }));
    }
    
    // If it's an object, convert to array
    return Object.entries(evalData.categoryScores).map(([key, score]) => ({
      category: key.replace(/([A-Z])/g, ' $1').trim(), // Convert camelCase to Title Case
      score: score,
      maxScore: 10, // Category scores are always out of 10
      feedback: evalData.detailedFeedback?.[key] || evalData.feedback?.[key] || null
    }));
  };

  const categoryScoresArray = getCategoryScoresArray();

  // Parse talking ratio
  const getTalkingRatio = () => {
    if (!evalData.talkingRatio) return { prospect: 0, salesperson: 0 };
    
    // If it's already an object with prospect/salesperson
    if (typeof evalData.talkingRatio === 'object' && evalData.talkingRatio.prospect !== undefined) {
      return evalData.talkingRatio;
    }
    
    // If it's a number (0-1 representing salesperson percentage)
    if (typeof evalData.talkingRatio === 'number') {
      const salespersonPct = Math.round(evalData.talkingRatio * 100);
      return {
        salesperson: salespersonPct,
        prospect: 100 - salespersonPct
      };
    }
    
    // If it's a string like "0.45"
    if (typeof evalData.talkingRatio === 'string') {
      const parsed = parseFloat(evalData.talkingRatio);
      if (!isNaN(parsed)) {
        const salespersonPct = Math.round(parsed * 100);
        return {
          salesperson: salespersonPct,
          prospect: 100 - salespersonPct
        };
      }
    }
    
    return { prospect: 0, salesperson: 0 };
  };

  const talkingRatio = getTalkingRatio();

  // Check if evaluation has expired (no re-categorization after expiration)
  const isExpired = () => {
    if (!evaluation.expiresAt) return false;
    const expireDate = evaluation.expiresAt.toDate ? evaluation.expiresAt.toDate() : new Date(evaluation.expiresAt);
    return new Date() > expireDate;
  };

  const expired = isExpired();

  const handleRecategorize = async () => {
    setIsRecategorizing(true);
    setRecategorizeError(null);
    setRecategorizeSuccess(null);

    try {
      const recategorizeFunction = httpsCallable(functions, 'recategorizeAndReEvaluate');
      const result = await recategorizeFunction({
        evaluationId: evaluation.id,
        newCallType: newCallType,
        teamId: evaluation.teamId,
      });

      const data = result.data;
      setRecategorizeSuccess(
        `Re-evaluated! Score changed from ${data.previousScore} to ${data.newScore} (${data.scoreChange > 0 ? '+' : ''}${data.scoreChange} points)`
      );
      setShowRecategorize(false);

      // Notify parent to refresh the evaluation
      if (onEvaluationUpdate) {
        onEvaluationUpdate();
      }

      // Auto-hide success message after 3 seconds (keep modal open)
      setTimeout(() => {
        setRecategorizeSuccess(null);
        setShowRecategorize(false);
      }, 3000);

    } catch (error) {
      console.error('Error re-categorizing:', error);
      setRecategorizeError('Something went wrong during re-evaluation. Please try again.');
      createAutoReport({
        errorType: 're_evaluation_failed',
        errorMessage: error.message || 'Unknown re-categorization error',
        userEmail,
        userRole,
        teamId: evaluation.teamId,
        context: {
          evaluationId: evaluation.id,
          callType: evaluation.callType,
          newCallType,
          action: 'recategorize_and_re_evaluate',
        },
      });
    } finally {
      setIsRecategorizing(false);
    }
  };
  
  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Call Evaluation — ${salespersonDisplayName}`}
      size="xl"
      showCloseButton={false}
    >
        {/* Custom Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-lg -mt-2 mb-6">
          <div className="flex justify-between items-start mb-3">
            <div className="text-white">
              <div className="text-xs opacity-90 mb-1">Call Evaluation</div>
              <h2 className="text-lg font-bold mb-1">
                Salesperson: {salespersonDisplayName}
              </h2>
              <h3 className="text-base mb-1">
                Prospect: {evaluation.prospect}
              </h3>
              <p className="text-blue-100 text-sm">
                {formatDate(evaluation.uploadedAt)}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => exportEvaluationToPDF(evaluation, formatDate)}
                style={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.2)' }}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              >
                Download PDF
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                style={{ color: 'white' }}
                aria-label="Close report"
              >
                ×
              </Button>
            </div>
          </div>
          
          {/* Overall Score */}
          <div className="mt-3 text-center">
            <div className="text-white">
              <div className="text-4xl font-bold">{evalData.overallScore}/100</div>
              <div className="text-sm mt-1 uppercase tracking-wide">Overall Sales Call Score</div>
              {evalData.shortCall && (
                <div className="text-xs mt-2 bg-white/20 rounded-full px-3 py-1 inline-block">
                  📞 Short Call • {evalData.categoriesScored || '?'}/10 categories scored
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div>
          {/* Call Type & Re-categorize Section */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-600 mb-1">Call Type</div>
                <div className="text-lg font-bold text-blue-700">
                  {getCallTypeLabel(evaluation.callType, industryId)}
                </div>
              </div>
              
              {isAdmin && !showRecategorize && !expired && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowRecategorize(true)}
                >
                  Re-categorize & Re-evaluate
                </Button>
              )}
              
              {expired && (
                <div className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm font-medium">
                  ⏰ Expired - Re-evaluation unavailable
                </div>
              )}
            </div>

            {showRecategorize && !expired && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Change call type and re-evaluate with new criteria:
                </div>
                <div className="flex items-center space-x-3">
                  <select
                    value={newCallType}
                    onChange={(e) => setNewCallType(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isRecategorizing}
                  >
                    {callTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleRecategorize}
                    loading={isRecategorizing}
                    disabled={isRecategorizing}
                  >
                    {isRecategorizing ? 'Processing...' : 'Re-evaluate'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowRecategorize(false);
                      setRecategorizeError(null);
                      setNewCallType(evaluation.callType);
                    }}
                    disabled={isRecategorizing}
                  >
                    Cancel
                  </Button>
                </div>
                {recategorizeError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">❌ {recategorizeError}</p>
                  </div>
                )}
              </div>
            )}

            {recategorizeSuccess && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium">✅ {recategorizeSuccess}</p>
                <p className="text-xs text-green-600 mt-1">Closing in 3 seconds...</p>
              </div>
            )}
          </div>
          {/* Linked Lead Card */}
          {(evaluation?.linkedLeadId || linkedLead) ? (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-600 font-bold text-sm">🔗 Linked Lead</span>
                <button
                  onClick={handleUnlinkLead}
                  disabled={linkingLead}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                  title="Unlink this lead"
                  aria-label="Unlink lead from this evaluation"
                >
                  {linkingLead ? '...' : '✕ Unlink'}
                </button>
              </div>
              {loadingLinkedLead ? (
                <p className="text-sm text-gray-500">Loading lead data...</p>
              ) : linkedLead ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>{' '}
                    <span className="font-medium">{linkedLead.prospectName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span>{' '}
                    <span className="font-medium">{linkedLead.timestamp?.toLocaleDateString() || 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Lead not found (ID: {evaluation.linkedLeadId})</p>
              )}
            </div>
          ) : (
            /* No lead linked — show link button/picker */
            <div className="mb-6">
              {showLeadPicker ? (
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Link to a lead:</div>
                  <select
                    onChange={(e) => { if (e.target.value) handleLinkLead(e.target.value); }}
                    disabled={linkingLead || loadingLeads}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    defaultValue=""
                  >
                    <option value="" disabled>{loadingLeads ? 'Loading leads...' : 'Choose a lead...'}</option>
                    {recentLeads
                      .filter(l => !l.linkedEvaluationId)
                      .map(l => (
                        <option key={l.id} value={l.id}>
                          {l.prospectName || l.prospectPhone || 'Unnamed'} — {l.timestamp?.toLocaleDateString() || 'N/A'}
                        </option>
                      ))
                    }
                  </select>
                  {linkingLead && <p className="text-xs text-blue-500 mt-1">Linking...</p>}
                  <button
                    onClick={() => setShowLeadPicker(false)}
                    disabled={linkingLead}
                    className="mt-2 text-xs text-gray-400 hover:text-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowLeadPicker(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors"
                >
                  <span>🔗</span>
                  Link to a lead
                </button>
              )}
            </div>
          )}

          {/* Talking Ratio */}
          {(talkingRatio.prospect > 0 || talkingRatio.salesperson > 0) && (
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-3 uppercase tracking-wide">Talking Ratio</h3>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-2 font-medium">
                    <span>Prospect: {talkingRatio.prospect}%</span>
                    <span>Salesperson: {talkingRatio.salesperson}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-6 flex overflow-hidden">
                    <div 
                      className="bg-green-500 flex items-center justify-center text-white text-xs font-bold"
                      style={{ width: `${talkingRatio.prospect}%` }}
                    >
                      {talkingRatio.prospect > 15 && `${talkingRatio.prospect}%`}
                    </div>
                    <div 
                      className="bg-blue-500 flex items-center justify-center text-white text-xs font-bold"
                      style={{ width: `${talkingRatio.salesperson}%` }}
                    >
                      {talkingRatio.salesperson > 15 && `${talkingRatio.salesperson}%`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Verdict */}
          <div className="mb-8 border-l-4 border-blue-500 pl-4 bg-gray-50 p-4 rounded">
            <h3 className="text-lg font-bold mb-3 uppercase tracking-wide">📋 Final Verdict</h3>
            <p className="text-gray-700 whitespace-pre-line leading-relaxed">{evalData.verdict}</p>
          </div>
          
          {/* Score Breakdown */}
          {categoryScoresArray.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wide">📊 Score Breakdown By Category</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categoryScoresArray.map((cat, idx) => {
                  const isNullScore = cat.score === null || cat.score === undefined;
                  const score = cat.score || 0;
                  const percentage = isNullScore ? 0 : Math.round((score / (cat.maxScore || 10)) * 100);
                  
                  // Color coding: 0-4 red, 5-7 yellow, 8-10 green
                  const getScoreColor = () => {
                    if (isNullScore) return 'text-gray-500 bg-gray-50 border-gray-400';
                    if (score >= 8) return 'text-green-600 bg-green-50 border-green-500';
                    if (score >= 5) return 'text-yellow-600 bg-yellow-50 border-yellow-500';
                    return 'text-red-600 bg-red-50 border-red-500';
                  };
                  
                  const getBarColor = () => {
                    if (score >= 8) return 'bg-green-500';
                    if (score >= 5) return 'bg-yellow-500';
                    return 'bg-red-500';
                  };

                  return (
                    <div key={idx} className={`${getScoreColor()} border-l-4 pl-4 p-3 rounded-r-lg`}>
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-bold text-sm">{cat.category}</h4>
                        <span className="text-xl font-bold">
                          {isNullScore ? (
                            <span className="text-gray-400 italic">N/A</span>
                          ) : (
                            <>{cat.score}<span className="text-sm">/{cat.maxScore || 10}</span></>
                          )}
                        </span>
                      </div>
                      {!isNullScore && (
                        <div className="w-full bg-gray-300 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${getBarColor()}`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      )}
                      {cat.feedback && (
                        <div className="text-xs text-gray-700 mt-2 leading-relaxed">
                          {typeof cat.feedback === 'string' ? (
                            cat.feedback
                          ) : Array.isArray(cat.feedback) ? (
                            <ul className="list-disc list-inside space-y-1">
                              {cat.feedback.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            JSON.stringify(cat.feedback)
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary Section */}
          {evalData.summary && (
            <div className="mb-8 bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wide">📝 Summary</h3>
              <p className="text-gray-700 whitespace-pre-line leading-relaxed">{evalData.summary}</p>
            </div>
          )}

          {/* Detailed Feedback */}
          {evalData.detailedFeedback && Object.keys(evalData.detailedFeedback).length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wide">💬 Detailed Feedback</h3>
              <div className="space-y-4">
                {Object.entries(evalData.detailedFeedback).map(([key, feedback], idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-lg border-l-4 border-purple-500">
                    <h4 className="font-bold text-gray-900 mb-2">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{feedback}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Evaluation Summary (if available) */}
          {(evalData.evaluation?.didWell || evalData.evaluation?.minorImprovements || 
            evalData.evaluation?.majorMistakes || evalData.evaluation?.lostConnection) && (
            <div className="mb-8 bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wide">📊 Evaluation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-green-700 mb-3 flex items-center">
                    <span className="text-xl mr-2">✅</span> What They Did Well
                  </h4>
                  {evalData.evaluation?.didWell && evalData.evaluation.didWell.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                      {evalData.evaluation.didWell.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">None</p>
                  )}
                </div>
                
                <div>
                  <h4 className="font-semibold text-yellow-700 mb-3 flex items-center">
                    <span className="text-xl mr-2">⚠️</span> Minor Improvements
                  </h4>
                  {evalData.evaluation?.minorImprovements && evalData.evaluation.minorImprovements.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                      {evalData.evaluation.minorImprovements.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">None</p>
                  )}
                </div>
                
                <div>
                  <h4 className="font-semibold text-red-700 mb-3 flex items-center">
                    <span className="text-xl mr-2">❌</span> Major Mistakes
                  </h4>
                  {evalData.evaluation?.majorMistakes && evalData.evaluation.majorMistakes.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                      {evalData.evaluation.majorMistakes.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">None</p>
                  )}
                </div>
                
                <div>
                  <h4 className="font-semibold text-purple-700 mb-3 flex items-center">
                    <span className="text-xl mr-2">💔</span> Lost Connection Points
                  </h4>
                  {evalData.evaluation?.lostConnection && evalData.evaluation.lostConnection.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                      {evalData.evaluation.lostConnection.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">None</p>
                  )}
                </div>
              </div>
            </div>
          )}

          
          {/* Strategy Checklist */}
          {evalData.strategyChecklist && evalData.strategyChecklist.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-wide">Strategy Checklist (Pass/Fail)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {evalData.strategyChecklist.map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                    <span className={`text-2xl ${item.passed ? 'text-green-600' : 'text-red-600'}`}>
                      {item.passed ? '☑' : '☐'}
                    </span>
                    <span className="text-sm">{item.item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Key Tactics & Errors */}
          {(evalData.keyTactics || evalData.unforcedErrors) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-bold text-green-800 mb-3">Top 3 Key Tactics Observed</h3>
                {evalData.keyTactics && evalData.keyTactics.length > 0 ? (
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                    {evalData.keyTactics.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-gray-500 italic">None</p>
                )}
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-bold text-red-800 mb-3">Top 3 Unforced Errors Observed</h3>
                {evalData.unforcedErrors && evalData.unforcedErrors.length > 0 ? (
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                    {evalData.unforcedErrors.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-gray-500 italic">None</p>
                )}
              </div>
            </div>
          )}
          
          {/* Prospect Takeaways */}
          {evalData.prospectTakeaways && (
            <div className="mb-8 bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 text-lg">🎯 Prospect Takeaways</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {evalData.prospectTakeaways.problem && (
                  <div>
                    <div className="font-semibold text-blue-800 text-sm mb-1">Core Problem:</div>
                    <p className="text-sm text-gray-700">{evalData.prospectTakeaways.problem}</p>
                  </div>
                )}
                {evalData.prospectTakeaways.motivation && (
                  <div>
                    <div className="font-semibold text-blue-800 text-sm mb-1">Motivation:</div>
                    <p className="text-sm text-gray-700">{evalData.prospectTakeaways.motivation}</p>
                  </div>
                )}
                {evalData.prospectTakeaways.emotions && (
                  <div>
                    <div className="font-semibold text-blue-800 text-sm mb-1">Emotions:</div>
                    <p className="text-sm text-gray-700">{evalData.prospectTakeaways.emotions}</p>
                  </div>
                )}
                {evalData.prospectTakeaways.timeline && (
                  <div>
                    <div className="font-semibold text-blue-800 text-sm mb-1">Timeline:</div>
                    <p className="text-sm text-gray-700">{evalData.prospectTakeaways.timeline}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Missed Opportunities */}
          {evalData.missedOpportunities && (
            <div className="mb-8 bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
              <h3 className="font-bold text-orange-900 mb-4 text-lg">💡 3 Missed Opportunities</h3>
              {evalData.missedOpportunities.length > 0 ? (
                <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                  {evalData.missedOpportunities.map((item, idx) => (
                    <li key={idx} className="leading-relaxed">{item}</li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-gray-500 italic">None</p>
              )}
            </div>
          )}
          
          {/* Tactical Next Steps */}
          {evalData.tacticalNextSteps && (
            <div className="mb-8 bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
              <h3 className="font-bold text-purple-900 mb-4 text-lg">🎯 3 Tactical Next Steps</h3>
              {evalData.tacticalNextSteps.length > 0 ? (
                <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                  {evalData.tacticalNextSteps.map((item, idx) => (
                    <li key={idx} className="leading-relaxed">{item}</li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-gray-500 italic">None</p>
              )}
            </div>
          )}
          
          {/* Final Learning */}
          {evalData.finalLearning && (
            <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-4 rounded-lg border-2 border-purple-300">
              <h3 className="font-bold text-purple-900 mb-3 text-lg">🎓 Final Learning</h3>
              <p className="text-gray-800 font-medium text-lg leading-relaxed">{evalData.finalLearning}</p>
            </div>
          )}
        </div>
    </Modal>
  );
}

